import { useMemo, useState } from "react";

export type CalculationMode = "weight_pieces" | "fraction_volume";

interface UseOpenedPackCalculatorProps {
  uomName: string;
  initialActualQty: number;
  initialPackSize?: number | null;
  onApply: (totalQty: number, breakdownNote: string) => void;
}

export function useOpenedPackCalculator({
  uomName,
  initialActualQty,
  initialPackSize,
  onApply,
}: UseOpenedPackCalculatorProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Auto-detect sensible default pack size based on UOM or connected purchased item
  const defaultPackSize = useMemo(() => {
    if (initialPackSize && initialPackSize > 0) return initialPackSize;
    const uom = uomName.toLowerCase();
    if (uom.includes("gram") || uom === "g") return 1000;
    if (uom.includes("ml") || uom.includes("mili")) return 750;
    if (uom.includes("pcs") || uom.includes("cup") || uom.includes("unit"))
      return 50;
    return 1;
  }, [uomName, initialPackSize]);

  const defaultMode: CalculationMode = useMemo(() => {
    const uom = uomName.toLowerCase();
    if (
      uom.includes("botol") ||
      uom.includes("btl") ||
      uom.includes("liter") ||
      uom === "l"
    ) {
      return "fraction_volume";
    }
    return "weight_pieces";
  }, [uomName]);

  const [mode, setMode] = useState<CalculationMode>(defaultMode);
  const [sealedQty, setSealedQty] = useState<number>(0);
  const [packSize, setPackSize] = useState<number>(defaultPackSize);
  const [looseQty, setLooseQty] = useState<number>(0);
  const [fraction, setFraction] = useState<number>(0);

  // Calculate total
  const calculatedTotal = useMemo(() => {
    if (mode === "weight_pieces") {
      if (packSize > 0) {
        return Number((sealedQty * packSize + looseQty).toFixed(2));
      }
      return Number((sealedQty + looseQty).toFixed(2));
    } else {
      // fraction_volume
      if (packSize > 1) {
        // e.g. Base unit is mL, pack is 750 mL
        return Number((sealedQty * packSize + fraction * packSize).toFixed(2));
      } else {
        // Base unit is Bottle/Pack, fraction is 0.5
        return Number((sealedQty + fraction).toFixed(2));
      }
    }
  }, [mode, sealedQty, packSize, looseQty, fraction]);

  // Formula description
  const summaryFormula = useMemo(() => {
    if (mode === "weight_pieces") {
      if (packSize > 1) {
        return `(${sealedQty} pack × ${packSize.toLocaleString("id-ID")}) + ${looseQty.toLocaleString("id-ID")} = ${calculatedTotal.toLocaleString("id-ID")} ${uomName}`;
      }
      return `${sealedQty} + ${looseQty} = ${calculatedTotal} ${uomName}`;
    } else {
      if (packSize > 1) {
        const partialMl = fraction * packSize;
        return `(${sealedQty} pack × ${packSize.toLocaleString("id-ID")}) + ${partialMl.toLocaleString("id-ID")} (${fraction} btl) = ${calculatedTotal.toLocaleString("id-ID")} ${uomName}`;
      }
      return `${sealedQty} botol + ${fraction} botol = ${calculatedTotal} ${uomName}`;
    }
  }, [mode, sealedQty, packSize, looseQty, fraction, calculatedTotal, uomName]);

  // Breakdown note for reason
  const breakdownNote = useMemo(() => {
    if (mode === "weight_pieces") {
      if (packSize > 1 && looseQty > 0) {
        return `${sealedQty} pack utuh + ${looseQty} ${uomName} terbuka`;
      }
      if (looseQty > 0) {
        return `Sisa terbuka ${looseQty} ${uomName}`;
      }
    } else {
      if (fraction > 0) {
        return `${sealedQty} utuh + ${fraction} botol terbuka`;
      }
    }
    return "";
  }, [mode, sealedQty, packSize, looseQty, fraction, uomName]);

  // Handlers
  const handleOpen = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      // If initialActualQty is set, populate sensible starting point
      if (initialActualQty > 0 && packSize > 0) {
        const whole = Math.floor(initialActualQty / packSize);
        const rem = Number((initialActualQty % packSize).toFixed(2));
        setSealedQty(whole);
        setLooseQty(rem);
      }
    }
  };

  const handleApply = () => {
    onApply(calculatedTotal, breakdownNote);
    setIsOpen(false);
  };

  const setPackSizePreset = (size: number) => {
    setPackSize(size);
  };

  const setFractionPreset = (frac: number) => {
    setFraction(frac);
  };

  return {
    isOpen,
    setIsOpen: handleOpen,
    mode,
    setMode,
    sealedQty,
    setSealedQty,
    packSize,
    setPackSize,
    looseQty,
    setLooseQty,
    fraction,
    setFraction,
    calculatedTotal,
    summaryFormula,
    breakdownNote,
    handleApply,
    setPackSizePreset,
    setFractionPreset,
  };
}
